import { ref } from 'vue';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useModal } from '~/composables/utils/useModal';
import { usePageData } from '~/composables/usePageData';
import { useEnvProps } from '~/composables/useEnvProps';
import useMarxent from '~/modules/marxent/composables/useMarxent';
import useMarxentButton from '~/modules/marxent/composables/useMarxentButton';
import { fireLinkTag } from '~/features/analytics/tagManager';
import { urlContainsMarxentCTATrue } from '~/utils/OSVersionSupportedUtil';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';

/**
 * @typedef {Object} Returns
 * @property {boolean} isArEligible
 * @property {boolean} isAdded
 * @property {function} setCtaConfig
 * @property {function} onMarxentButtonClick
 * @property {object} marxentCtaConfig
 */

/**
 * Get the QR URL for a specific SKU and API credentials.
 * @returns {{
 *  isArEligible: import('vue').ComputedRef<boolean>,
 *  isAdded: import('vue').Ref<boolean>,
 *  marxentCtaConfig: import('vue').Ref<Object>,
 *  setCtaConfig: function,
 *  onMarxentButtonClick: function,
 * }}.
 */

export default function useMarxentCta() {
  const { isDesktop } = useDeviceType();
  const { productId } = usePageData();
  const selectedMemberId = useSelectedMemberId();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const { selectedUpcNumber } = useUpc(isShopTheSetExpEnabled ? selectedMemberId : productId);
  const { open } = useModal();
  const { marxentApiCredentials } = useEnvProps();
  const { initMarxent } = useMarxent();
  const { marxentValues, checkForMarxentAssetsAndUrl, isArEligible, isArReady, marxentButtonStyles } = useMarxentButton(
    initMarxent,
    marxentApiCredentials,
  );

  const marxentCtaConfig = ref({});
  const isAdded = ref(false);

  // Flag to control auto-start behavior.
  const shouldAutoStartMarxent = ref(true);

  const onMarxentButtonClick = async () => {
    // Desktop case
    if (isDesktop.value && isArEligible.value) {
      open('marxent'); // opens marxent qr modal
      // For Mobile we're using an a tag with href
      const data = {
        event_name: 'marxent qr code open',
        custom_link_name: 'marxent qr code open',
      };
      fireLinkTag(data);
    }
  };

  const setCtaConfig = async () => {
    await checkForMarxentAssetsAndUrl(selectedUpcNumber.value);
    if (isArReady.value && isArEligible.value) {
      marxentCtaConfig.value = {
        customCssClass: 'marxent font-small',
        customStyle: marxentButtonStyles,
        id: 'marxent',
        label: 'VIEW IN YOUR ROOM',
        isAR: !isDesktop.value,
        onClickHandler: onMarxentButtonClick,
        url: isDesktop.value ? null : marxentValues.value?.marxentUrl,
      };
    }
  };

  /**
   * Triggers the Marxent experience by programmatically clicking the hyperlink.
   */
  const autoStartMarxentExperience = () => {
    const hasMarxentFlag = urlContainsMarxentCTATrue();
    if (hasMarxentFlag) {
      // when a user scans the QR code to get url, marxent experience needs to auto start
      const marxentHrefBtn = document.querySelector('#view-in-ar');
      if (shouldAutoStartMarxent.value && marxentHrefBtn) {
        marxentHrefBtn.click();
        shouldAutoStartMarxent.value = false;
      }
    }
  };

  return {
    setCtaConfig,
    isArEligible,
    marxentCtaConfig,
    isAdded,
    onMarxentButtonClick,
    autoStartMarxentExperience,
  };
}
